import { Component, OnInit } from '@angular/core';
import { tap } from 'rxjs/operators';
import { CompanyService } from 'src/app/core/services/company.service';
import { PusherService } from 'src/app/core/services/pusher.service';
import { BaseComponent } from 'src/app/core/infrastructure/classes/base-component';
import { ProcessingService } from 'src/app/core/services/processing.service';
import { CompanyType } from 'src/app/core/infrastructure/enums/company-type.enum';
import { HeaderService } from 'src/app/core/services/header.service';
import { Company } from 'src/app/core/models/company';
import { AuthenticationService, ModalService } from '@stockaid/services';
import { GenericModalComponent } from 'src/app/theme/shared/components/modal/generic-modal/generic-modal.component';
import { AscV2Service } from 'src/app/core/services/v2/asc.service';
import Swal from 'sweetalert2';
import { NotificationKey } from 'src/app/core/infrastructure/enums/notification.enum';

@Component({
  selector: 'app-nav-lost-sale',
  templateUrl: './nav-lost-sale.component.html',
  styleUrls: ['./nav-lost-sale.component.scss'],
})
export class NavLostSaleComponent extends BaseComponent implements OnInit {
  lastCalculated: Date;
  isCalculateLSInProcess = false;
  currentCompany: Company;
  willRemindToRunCalculate = true;
  isProcessing = false;
  message: string;
  readonly CompanyType = CompanyType;

  get isCurrentCompanyASC(): boolean {
    return this.currentCompany?.companyType === CompanyType.ASC;
  }

  constructor(
    private companyService: CompanyService,
    private pusherService: PusherService,
    private processingService: ProcessingService,
    private headerService: HeaderService,
    private modalService: ModalService,
    private ascService: AscV2Service,
    private authService: AuthenticationService,
  ) {
    super();
  }

  ngOnInit() {
    this.pusherService.userChannel.bind('notification', (data) => {
      if (data?.companyKey !== this.currentCompany?.companyKey) {
        return;
      }
      if (data?.key !== NotificationKey.lostSalesCalculateStart) {
        return;
      }

      switch (data.type) {
        case 'header':
        case 'title':
          this.message = data.msg;
          this.isCalculateLSInProcess = true;
          this.isProcessing = true;
          break;
        case 'message':
          break;
        case 'notice': 
        case 'close':
          if (data.key === NotificationKey.lostSalesCalculateStart && data.type === 'close') {
            Swal.fire({
              html: `
                <div>Calculate lost sales successfully</div>
                <div>Please run forecast to see the new updates.</div>
              `,
              type: 'success',
              showCloseButton: true,
              allowOutsideClick: false,
            });
          }
          this.isProcessing = false;
          this.isCalculateLSInProcess = false;
          this.getCurrentCompany(true).subscribe();
          break;
        default:
          throw Error('message type not accounted for.');
      }

       if (!this.isProcessing) {
        this.message = '';
      }
    });

    this.processingService.processingStartedSync
      .pipe(this.autoCleanUp())
      .subscribe((data) => {
        this.isCalculateLSInProcess = data;
      });

    this.processingService.processingStoppedSync
      .pipe(this.autoCleanUp())
      .subscribe((data) => {
        this.isCalculateLSInProcess = data;
      });

    this.headerService
      .getCompany()
      .pipe(this.autoCleanUp())
      .subscribe((data) => {
        this.isCalculateLSInProcess = data?.jobInitiator ? true : false;
      });

    this.getCurrentCompany().subscribe();

    this.headerService.notifyCalculateSubject
      .pipe(this.autoCleanUp())
      .subscribe((willRemind) => this.willRemindToRunCalculate = willRemind);
  }

  getCurrentCompany(getNew = false) {
    const getCompanyFunction = getNew
      ? this.companyService.getById(this.currentCompany.companyKey)
      : this.companyService.getCurrentCompany();

    return getCompanyFunction.pipe(
      this.autoCleanUp(),
      tap((company) => {
        this.currentCompany = company;
        this.lastCalculated = company.lastCalculatedLostSaleDate;
        this.willRemindToRunCalculate = Boolean(company.isLostSalesSettingsChange);
      })
    );
  }

  togglePopup() {
    if (this.isCalculateLSInProcess) {
      return;
    }

    const iconHTML = `<svg width="24" height="24" viewBox="0 0 43 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M36 8C36 6.67392 35.4732 5.40215 34.5355 4.46447C33.5979 3.52678 32.3261 3 31 3H9C7.67392 3 6.40215 3.52678 5.46447 4.46447C4.52678 5.40215 4 6.67392 4 8V38C4 39.3261 4.52678 40.5979 5.46447 41.5355C6.40215 42.4732 7.67392 43 9 43H31C32.3261 43 33.5979 42.4732 34.5355 41.5355C35.4732 40.5979 36 39.3261 36 38V8ZM34 38C34 38.7956 33.6839 39.5587 33.1213 40.1213C32.5587 40.6839 31.7956 41 31 41H9C8.20435 41 7.44129 40.6839 6.87868 40.1213C6.31607 39.5587 6 38.7956 6 38V8C6 7.20435 6.31607 6.44129 6.87868 5.87868C7.44129 5.31607 8.20435 5 9 5H31C31.7956 5 32.5587 5.31607 33.1213 5.87868C33.6839 6.44129 34 7.20435 34 8V38Z" fill="white"/>
          <path d="M30 7H10C9.46957 7 8.96086 7.21071 8.58579 7.58579C8.21071 7.96086 8 8.46957 8 9V15C8 15.5304 8.21071 16.0391 8.58579 16.4142C8.96086 16.7893 9.46957 17 10 17H30C30.5304 17 31.0391 16.7893 31.4142 16.4142C31.7893 16.0391 32 15.5304 32 15V9C32 8.46957 31.7893 7.96086 31.4142 7.58579C31.0391 7.21071 30.5304 7 30 7ZM30 15H10V9H30V15Z" fill="white"/>
          <path d="M14 26H10C9.73478 26 9.48043 26.1054 9.29289 26.2929C9.10536 26.4804 9 26.7348 9 27V31C9 31.2652 9.10536 31.5196 9.29289 31.7071C9.48043 31.8946 9.73478 32 10 32H14C14.2652 32 14.5196 31.8946 14.7071 31.7071C14.8946 31.5196 15 31.2652 15 31V27C15 26.7348 14.8946 26.4804 14.7071 26.2929C14.5196 26.1054 14.2652 26 14 26ZM13 30H11V28H13V30Z" fill="white"/>
          <path d="M14 33H10C9.73478 33 9.48043 33.1054 9.29289 33.2929C9.10536 33.4804 9 33.7348 9 34V38C9 38.2652 9.10536 38.5196 9.29289 38.7071C9.48043 38.8946 9.73478 39 10 39H14C14.2652 39 14.5196 38.8946 14.7071 38.7071C14.8946 38.5196 15 38.2652 15 38V34C15 33.7348 14.8946 33.4804 14.7071 33.2929C14.5196 33.1054 14.2652 33 14 33ZM13 37H11V35H13V37Z" fill="white"/>
          <path d="M14 19H10C9.73478 19 9.48043 19.1054 9.29289 19.2929C9.10536 19.4804 9 19.7348 9 20V24C9 24.2652 9.10536 24.5196 9.29289 24.7071C9.48043 24.8946 9.73478 25 10 25H14C14.2652 25 14.5196 24.8946 14.7071 24.7071C14.8946 24.5196 15 24.2652 15 24V20C15 19.7348 14.8946 19.4804 14.7071 19.2929C14.5196 19.1054 14.2652 19 14 19ZM13 23H11V21H13V23Z" fill="white"/>
          <path d="M22 26H18C17.7348 26 17.4804 26.1054 17.2929 26.2929C17.1054 26.4804 17 26.7348 17 27V31C17 31.2652 17.1054 31.5196 17.2929 31.7071C17.4804 31.8946 17.7348 32 18 32H22C22.2652 32 22.5196 31.8946 22.7071 31.7071C22.8946 31.5196 23 31.2652 23 31V27C23 26.7348 22.8946 26.4804 22.7071 26.2929C22.5196 26.1054 22.2652 26 22 26ZM21 30H19V28H21V30Z" fill="white"/>
          <path d="M22 33H18C17.7348 33 17.4804 33.1054 17.2929 33.2929C17.1054 33.4804 17 33.7348 17 34V38C17 38.2652 17.1054 38.5196 17.2929 38.7071C17.4804 38.8946 17.7348 39 18 39H22C22.2652 39 22.5196 38.8946 22.7071 38.7071C22.8946 38.5196 23 38.2652 23 38V34C23 33.7348 22.8946 33.4804 22.7071 33.2929C22.5196 33.1054 22.2652 33 22 33ZM21 37H19V35H21V37Z" fill="white"/>
          <path d="M22 19H18C17.7348 19 17.4804 19.1054 17.2929 19.2929C17.1054 19.4804 17 19.7348 17 20V24C17 24.2652 17.1054 24.5196 17.2929 24.7071C17.4804 24.8946 17.7348 25 18 25H22C22.2652 25 22.5196 24.8946 22.7071 24.7071C22.8946 24.5196 23 24.2652 23 24V20C23 19.7348 22.8946 19.4804 22.7071 19.2929C22.5196 19.1054 22.2652 19 22 19ZM21 23H19V21H21V23Z" fill="white"/>
          <path d="M30 19H26C25.7348 19 25.4804 19.1054 25.2929 19.2929C25.1054 19.4804 25 19.7348 25 20V24C25 24.2652 25.1054 24.5196 25.2929 24.7071C25.4804 24.8946 25.7348 25 26 25H30C30.2652 25 30.5196 24.8946 30.7071 24.7071C30.8946 24.5196 31 24.2652 31 24V20C31 19.7348 30.8946 19.4804 30.7071 19.2929C30.5196 19.1054 30.2652 19 30 19ZM29 23H27V21H29V23Z" fill="white"/>
          <path d="M25.0021 37.5098C25.002 37.1158 25.0329 36.7224 25.0944 36.3332C25.3818 34.4715 26.3652 32.7868 27.8475 31.6167C29.3297 30.4467 31.2013 29.8779 33.087 30.0242C34.9727 30.1705 36.7333 31.0212 38.0157 32.4057C39.2982 33.7902 40.0078 35.6063 40.0023 37.4899C40.0023 37.8839 39.9714 38.2773 39.9099 38.6665C39.6225 40.5282 38.6391 42.2129 37.1569 43.3829C35.6747 44.553 33.8031 45.1218 31.9173 44.9755C30.0316 44.8292 28.2711 43.9785 26.9886 42.594C25.7062 41.2094 24.9966 39.3934 25.0021 37.5098ZM38.9496 37.4975C38.9486 35.8827 38.3363 34.3277 37.235 33.1426C36.1337 31.9574 34.6243 31.2292 33.0077 31.1031C31.3912 30.9771 29.7864 31.4624 28.5133 32.4625C27.2402 33.4625 26.3925 34.9036 26.1391 36.4985C26.0863 36.8331 26.0599 37.1713 26.06 37.5101C26.0611 39.1248 26.6733 40.6798 27.7746 41.865C28.8759 43.0501 30.3854 43.7784 32.0019 43.9044C33.6184 44.0305 35.2232 43.5451 36.4963 42.5451C37.7694 41.5451 38.6171 40.1039 38.8705 38.509C38.9237 38.1718 38.9502 37.831 38.9496 37.4896L38.9496 37.4975Z" fill="white"/>
          <path d="M26.001 37.5084C26.0009 37.1667 26.0277 36.8255 26.081 36.4879C26.3302 34.8733 27.1826 33.4122 28.4675 32.3974C29.7523 31.3827 31.3746 30.8893 33.0092 31.0162C34.6438 31.143 36.1698 31.8808 37.2815 33.0815C38.3931 34.2823 39.0082 35.8573 39.0034 37.4909C39.0034 37.8326 38.9766 38.1738 38.9233 38.5114C38.6741 40.1259 37.8217 41.5871 36.5369 42.6018C35.252 43.6166 33.6297 44.11 31.9951 43.9831C30.3605 43.8563 28.8345 43.1185 27.7228 41.9178C26.6112 40.717 25.9961 39.142 26.001 37.5084Z" fill="#587f99"/>
          <path d="M34.7728 38.8512C34.7537 39.8908 33.989 40.6067 32.7168 40.6846L32.6942 41.9163L31.9942 41.9041L32.017 40.6619C31.2115 40.5558 30.4655 40.1907 29.8974 39.6246L30.4497 38.9971C30.9769 39.5282 31.6927 39.8429 32.4504 39.877C33.2207 39.8904 33.8087 39.5268 33.8204 38.8873C33.8339 38.1556 33.2356 37.903 32.2761 37.5914C31.1394 37.2136 30.1052 36.8481 30.1274 35.6348C30.1456 34.6452 30.8702 33.9075 32.147 33.8692L32.1707 32.5822L32.8707 32.5944L32.8463 33.9261C33.4882 34.048 34.0929 34.3116 34.6138 34.6967L34.0779 35.3192C33.5706 34.9454 32.9616 34.7252 32.3267 34.6858C31.597 34.6731 31.08 34.9985 31.0691 35.5933C31.0574 36.2302 31.6856 36.4754 32.5535 36.767C33.8327 37.1868 34.8023 37.538 34.7783 38.8434L34.7728 38.8512Z" fill="white"/>
        </svg>`

    const modalTitle = 'Calculate Lost Sales';
    const modalContent = `
      <div class="generic-modal__content text-center mb-3">Quickly calculate lost sales using inventory, demand history.</div>
      <div class="generic-modal__content text-center""><strong>Would you like to start the calculation now?</strong></div>
    `;
    const modalSuccessBtn = 'Start Calculate';
    const modalErrorBtn = 'Cancel';
    const modalSuccessHandle = () => this.startCalculate();


    this.modalService.open(GenericModalComponent, {
      data: {
        customIconHTML: iconHTML,
        title: modalTitle,
        content: modalContent,
        successBtn: modalSuccessBtn,
        errorBtn: modalErrorBtn,
        theme: 'light',
      },
      backdrop: 'static',
      onSuccess: modalSuccessHandle,
      modalDialogClass: 'sync-modal',
      backdropClass: 'sync-backdrop'
    });
  }

  startCalculate() {
    if (this.isCalculateLSInProcess || this.isProcessing) {
      return;
    }

    if(this.currentCompany.companyType !== CompanyType.ASC) return;

    this.ascService.calculateLostSales({company_id: this.currentCompany.companyKey, user_id: this.authService.currentUser.userId})
    .subscribe(() => {
      this.isProcessing = true;
    });
  }
}
